/**
 * Runtime configuration, fetched from `/config.json` — the client's pattern, so one `dist/` deploys to every
 * environment and only that file changes.
 *
 * 🔴 No secret belongs here. Every Auth0 field is a public identifier: an SPA has no client secret (PKCE
 * replaces it), and the domain and audience are visible in the authorize URL anyway.
 */

export type Environment = 'dev' | 'staging' | 'prod';

export interface Auth0Config {
  readonly domain: string;
  /** The Roxxy-Employee Auth0 application — not the merchant client's. */
  readonly clientId: string;
  readonly audience: string;
  /**
   * Optional, unlike the client. The client pins passwordless `email`; employees may sign in another way,
   * and an absent connection lets Universal Login offer whatever the application allows.
   */
  readonly connection: string | null;
}

export interface AppConfig {
  /** e.g. https://api3.roxxy.ai */
  readonly gatewayBaseUrl: string;
  readonly environment: Environment;
  readonly features: Readonly<Record<string, boolean>>;
  readonly auth0: Auth0Config;
  /**
   * The client app, e.g. https://move37.roxxy.ai — where "Try it on the guided page" opens. Optional: with
   * none configured the link is simply not offered.
   */
  readonly clientBaseUrl: string | null;
  /**
   * PLAN-08 §4 — a log-search URL with placeholders, filled in for each report: `{logFilter}` (see `logSearchUrl`),
   * `{conversationId}`, `{clientId}`, `{identityId}`, `{from}` / `{to}` (ISO) and `{fromMs}` / `{toMs}` (epoch ms,
   * which Grafana takes).
   * Optional: with none, a report shows its window and ids instead of a link.
   */
  readonly logSearchUrl: string | null;
}

export class ConfigLoadError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ConfigLoadError';
  }
}

const ENVIRONMENTS: readonly Environment[] = ['dev', 'staging', 'prod'];

function nonBlank(o: Record<string, unknown>, key: string): string | null {
  const v = o[key];
  return typeof v === 'string' && v.trim().length > 0 ? v : null;
}

export function parseConfig(raw: unknown): AppConfig {
  if (typeof raw !== 'object' || raw === null) {
    throw new ConfigLoadError('config.json is not an object');
  }
  const o = raw as Record<string, unknown>;

  const gatewayBaseUrl = nonBlank(o, 'gatewayBaseUrl');
  if (gatewayBaseUrl === null) {
    throw new ConfigLoadError('config.json missing gatewayBaseUrl');
  }

  const environment = o['environment'];
  if (
    typeof environment !== 'string' ||
    !(ENVIRONMENTS as readonly string[]).includes(environment)
  ) {
    throw new ConfigLoadError(`config.json environment must be one of ${ENVIRONMENTS.join('|')}`);
  }

  const features = o['features'] ?? {};
  if (typeof features !== 'object' || features === null || Array.isArray(features)) {
    throw new ConfigLoadError('config.json features must be an object');
  }
  for (const [k, v] of Object.entries(features)) {
    if (typeof v !== 'boolean') {
      throw new ConfigLoadError(`config.json features.${k} must be a boolean`);
    }
  }

  const auth0 = o['auth0'];
  if (typeof auth0 !== 'object' || auth0 === null || Array.isArray(auth0)) {
    throw new ConfigLoadError('config.json auth0 must be an object');
  }
  const a = auth0 as Record<string, unknown>;
  const domain = nonBlank(a, 'domain');
  const clientId = nonBlank(a, 'clientId');
  const audience = nonBlank(a, 'audience');
  if (domain === null || clientId === null || audience === null) {
    // A console that cannot sign in must say so, not fall through to bare 401s.
    throw new ConfigLoadError('config.json auth0 needs domain, clientId and audience');
  }

  return {
    gatewayBaseUrl: gatewayBaseUrl.replace(/\/+$/, ''),
    environment: environment as Environment,
    features: Object.freeze({ ...(features as Record<string, boolean>) }),
    auth0: Object.freeze({ domain, clientId, audience, connection: nonBlank(a, 'connection') }),
    clientBaseUrl: nonBlank(o, 'clientBaseUrl')?.replace(/\/+$/, '') ?? null,
    logSearchUrl: nonBlank(o, 'logSearchUrl'),
  };
}

export interface LogSearch {
  readonly conversationId: string | null;
  readonly clientId: number;
  readonly identityId: number;
  readonly fromUtc: string;
  readonly toUtc: string;
}

/**
 * Fills `logSearchUrl`'s placeholders. 🔑 Every value is URL-encoded, so a template written for a query string
 * cannot be broken by what is put into it.
 *
 * 🔑 `{logFilter}` is Grafana Logs Drilldown's `var-metadata` value: `ConversationId|=|…` when the report names a
 * conversation, else `ClientId|=|…` — a report about a conversation that never started has no conversation id to
 * search by, and an empty one would match nothing. Both are attributes `SessionLogScope` puts on every session line.
 */
export function logSearchUrl(template: string, s: LogSearch): string {
  const values: Record<string, string> = {
    logFilter:
      s.conversationId === null
        ? `ClientId|=|${String(s.clientId)}`
        : `ConversationId|=|${s.conversationId}`,
    conversationId: s.conversationId ?? '',
    clientId: String(s.clientId),
    identityId: String(s.identityId),
    from: s.fromUtc,
    to: s.toUtc,
    fromMs: String(Date.parse(s.fromUtc)),
    toMs: String(Date.parse(s.toUtc)),
  };
  return template.replace(/\{(\w+)\}/g, (whole, name: string) =>
    name in values ? encodeURIComponent(values[name] ?? '') : whole,
  );
}

/**
 * The guided page opened on one experience. 🔑 The client carries the path AND query through its sign-in round
 * trip (`appState.target`), so somebody who has to sign in first still lands on this experience afterwards.
 */
export function guidedPageUrl(clientBaseUrl: string, experienceCode: string): string {
  return `${clientBaseUrl}/guided?experience=${encodeURIComponent(experienceCode)}`;
}

let cached: AppConfig | null = null;

export async function loadConfig(): Promise<AppConfig> {
  if (cached !== null) {
    return cached;
  }
  const res = await fetch('/config.json', { cache: 'no-store' });
  if (!res.ok) {
    throw new ConfigLoadError(`config.json failed: ${res.status}`);
  }
  cached = parseConfig(await res.json());
  return cached;
}

/** Set once at bootstrap so API modules need no injection. */
export function currentConfig(): AppConfig {
  if (cached === null) {
    throw new ConfigLoadError('config has not been loaded');
  }
  return cached;
}

/** Test seam. */
export function setConfigForTests(config: AppConfig | null): void {
  cached = config;
}
